import React, { useEffect, useRef, useState } from "react";

const HANDLES = [
  "corner-nw",
  "edge-n",
  "corner-ne",
  "edge-e",
  "corner-se",
  "edge-s",
  "corner-sw",
  "edge-w",
];

const MIN_SIZE = 40;
const KEEP_ON_CANVAS_PX = 32;

function croppedImageStyle(crop) {
  if (
    !crop ||
    !(crop.width > 0) ||
    !(crop.height > 0) ||
    !(crop.imageWidth > 0) ||
    !(crop.imageHeight > 0)
  ) {
    return {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      pointerEvents: "none",
      display: "block",
    };
  }
  return {
    position: "absolute",
    width: `${(crop.imageWidth / crop.width) * 100}%`,
    height: `${(crop.imageHeight / crop.height) * 100}%`,
    left: `${-(crop.x / crop.width) * 100}%`,
    top: `${-(crop.y / crop.height) * 100}%`,
    objectFit: "fill",
    pointerEvents: "none",
    display: "block",
  };
}

/**
 * Movable image overlay on the editor canvas (store-backed transform).
 * Use showChrome={false} for clipped content, chromeOnly for off-canvas handles.
 */
const OverlayImageNode = ({
  layer,
  selected,
  showChrome = true,
  chromeOnly = false,
  canvasSize,
  onSelect,
  onTransformStart,
  onChange,
  onChangeEnd,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [liveRotation, setLiveRotation] = useState(null);
  const dragRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onChangeEndRef = useRef(onChangeEnd);
  onChangeRef.current = onChange;
  onChangeEndRef.current = onChangeEnd;

  useEffect(() => {
    if (!isDragging && !isResizing && !isRotating) return undefined;

    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.mode === "drag") {
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        let x = d.originX + dx;
        let y = d.originY + dy;
        if (d.canvasW > 0 && d.canvasH > 0) {
          const minX = KEEP_ON_CANVAS_PX - d.originW;
          const minY = KEEP_ON_CANVAS_PX - d.originH;
          const maxX = d.canvasW - KEEP_ON_CANVAS_PX;
          const maxY = d.canvasH - KEEP_ON_CANVAS_PX;
          x = Math.min(maxX, Math.max(minX, x));
          y = Math.min(maxY, Math.max(minY, y));
        }
        onChangeRef.current?.({
          x: Math.round(x),
          y: Math.round(y),
        });
        return;
      }
      if (d.mode === "rotate") {
        const currentAngle =
          Math.atan2(e.clientY - d.centerY, e.clientX - d.centerX) *
          (180 / Math.PI);
        let angleDiff = currentAngle - d.startAngle;
        let newRotation = d.initialRotation + angleDiff;
        newRotation = ((newRotation % 360) + 360) % 360;
        if (e.shiftKey) {
          newRotation = Math.round(newRotation / 45) * 45;
        }
        setLiveRotation(newRotation);
        onChangeRef.current?.({ rotation: Math.round(newRotation) });
        return;
      }
      if (d.mode === "resize") {
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        const aspect = d.originW / Math.max(1, d.originH);
        let x = d.originX;
        let y = d.originY;
        let width = d.originW;
        let height = d.originH;
        const handle = d.handle;

        switch (handle) {
          case "edge-e":
            width = Math.max(MIN_SIZE, d.originW + dx);
            break;
          case "edge-w":
            width = Math.max(MIN_SIZE, d.originW - dx);
            x = d.originX + (d.originW - width);
            break;
          case "edge-s":
            height = Math.max(MIN_SIZE, d.originH + dy);
            break;
          case "edge-n":
            height = Math.max(MIN_SIZE, d.originH - dy);
            y = d.originY + (d.originH - height);
            break;
          case "corner-se": {
            const sx = (d.originW + dx) / d.originW;
            const sy = (d.originH + dy) / d.originH;
            const scale = Math.max(MIN_SIZE / d.originW, (sx + sy) / 2);
            width = d.originW * scale;
            height = width / aspect;
            break;
          }
          case "corner-ne": {
            const sx = (d.originW + dx) / d.originW;
            const sy = (d.originH - dy) / d.originH;
            const scale = Math.max(MIN_SIZE / d.originW, (sx + sy) / 2);
            width = d.originW * scale;
            height = width / aspect;
            y = d.originY + (d.originH - height);
            break;
          }
          case "corner-sw": {
            const sx = (d.originW - dx) / d.originW;
            const sy = (d.originH + dy) / d.originH;
            const scale = Math.max(MIN_SIZE / d.originW, (sx + sy) / 2);
            width = d.originW * scale;
            height = width / aspect;
            x = d.originX + (d.originW - width);
            break;
          }
          case "corner-nw": {
            const sx = (d.originW - dx) / d.originW;
            const sy = (d.originH - dy) / d.originH;
            const scale = Math.max(MIN_SIZE / d.originW, (sx + sy) / 2);
            width = d.originW * scale;
            height = width / aspect;
            x = d.originX + (d.originW - width);
            y = d.originY + (d.originH - height);
            break;
          }
          default:
            break;
        }

        onChangeRef.current?.({
          x: Math.round(x),
          y: Math.round(y),
          width: Math.round(width),
          height: Math.round(height),
        });
      }
    };

    const onUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setIsRotating(false);
      setLiveRotation(null);
      dragRef.current = null;
      onChangeEndRef.current?.();
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // Intentionally omit onChange/onChangeEnd — read via refs so store updates
    // during drag do not re-bind listeners and thrash updates.
  }, [isDragging, isResizing, isRotating]);

  const startDrag = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    onTransformStart?.();
    dragRef.current = {
      mode: "drag",
      startX: e.clientX,
      startY: e.clientY,
      originX: layer.x,
      originY: layer.y,
      originW: layer.width,
      originH: layer.height,
      canvasW: canvasSize?.width || 0,
      canvasH: canvasSize?.height || 0,
    };
    setIsDragging(true);
  };

  const startResize = (e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    onTransformStart?.();
    dragRef.current = {
      mode: "resize",
      handle,
      startX: e.clientX,
      startY: e.clientY,
      originX: layer.x,
      originY: layer.y,
      originW: layer.width,
      originH: layer.height,
    };
    setIsResizing(true);
  };

  const startRotate = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    onTransformStart?.();
    const el = e.currentTarget.closest(".overlay-image-node");
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const startAngle =
      Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    dragRef.current = {
      mode: "rotate",
      centerX,
      centerY,
      startAngle,
      initialRotation: Number(layer.rotation) || 0,
    };
    setLiveRotation(Number(layer.rotation) || 0);
    setIsRotating(true);
  };

  if (!layer?.visible || !layer?.imageUrl) return null;

  const displayRotation =
    liveRotation != null ? liveRotation : Number(layer.rotation) || 0;
  const hasCrop = Boolean(layer.contentCrop?.width && layer.contentCrop?.height);

  const nodeStyle = {
    position: "absolute",
    left: `${layer.x}px`,
    top: `${layer.y}px`,
    width: `${layer.width}px`,
    height: `${layer.height}px`,
    transform: `rotate(${displayRotation}deg) scale(${layer.flipH ? -1 : 1}, ${
      layer.flipV ? -1 : 1
    })`,
    transformOrigin: "center center",
    zIndex: (layer.zIndex ?? 1) + 10,
    cursor: chromeOnly ? "default" : isDragging ? "grabbing" : "grab",
    userSelect: "none",
    pointerEvents: chromeOnly ? "none" : "auto",
    // Keep overflow visible so rotate handle (above the box) is not clipped.
    overflow: "visible",
  };

  const chrome = showChrome && selected && (
    <>
      {!isResizing && (
        <>
          <div className="overlay-image-node__box" aria-hidden="true" />
          <div
            className="object-rotate-handle"
            onMouseDown={startRotate}
            title={`Rotate: ${Math.round(displayRotation)}°`}
          >
            <div className="rotate-handle-line" />
            <div className="rotate-handle-circle">↻</div>
          </div>
          {isRotating && (
            <div className="rotation-angle-display">
              {Math.round(displayRotation)}°
            </div>
          )}
        </>
      )}
      {!isRotating &&
        HANDLES.map((handle) => (
          <div
            key={handle}
            className={`object-resize-handle ${
              handle.startsWith("corner") ? "corner-handle" : "edge-handle"
            } ${handle}`}
            onMouseDown={(e) => startResize(e, handle)}
          />
        ))}
    </>
  );

  if (chromeOnly) {
    if (!selected) return null;
    return (
      <div
        className="overlay-image-node overlay-image-node--chrome overlay-image-node--selected"
        style={nodeStyle}
      >
        {chrome}
      </div>
    );
  }

  return (
    <div
      className={`overlay-image-node${selected && showChrome ? " overlay-image-node--selected" : ""}${
        hasCrop ? " overlay-image-node--cropped" : ""
      }`}
      style={nodeStyle}
      onMouseDown={startDrag}
    >
      <div
        className="overlay-image-node__media"
        style={{
          position: "absolute",
          inset: 0,
          overflow: hasCrop ? "hidden" : "visible",
          pointerEvents: "none",
        }}
      >
        <img
          src={layer.imageUrl}
          alt={layer.label || ""}
          draggable="false"
          style={croppedImageStyle(layer.contentCrop)}
        />
      </div>
      {chrome}
    </div>
  );
};

export default OverlayImageNode;
